import React, { FunctionComponent, useEffect, useState } from 'react';
import { History } from 'history';
import { InjectedIntlProps } from 'react-intl';

import { Col, Row } from 'react-grid-system';
import { Button } from '@nbc-design/button';
import { Card } from '@nbc-design/card';
import { Heading } from '@nbc-design/heading';
import { Bank } from '@nbc-design/icons/lib/web/Bank';
import { FinancialGrowthColor } from '@nbc-design/icons/lib/web/FinancialGrowthColor';
import { RelocatingEmployee } from '@nbc-design/icons/lib/web/RelocatingEmployee';
import { Selfie } from '@nbc-design/icons/lib/web/Selfie';
import { Link } from '@nbc-design/link';
import { Text } from '@nbc-design/text';

import Modal from 'components/Modal/Modal';
import { sendClickAnalyticsEvent, sendStepLoadedEvent } from 'services/analytics';
import { EVENT_IDS } from 'services/analytics/constants';
import { redirectToUnifiedLogin } from 'services/ssoRedirectService';
import { manageAutomaticRedirect } from 'services/ssoRedirectService/helper';
import { getBncId } from 'utils';
import { focusBody, scrollToTop } from 'utils/domUtils';
import { ROUTE } from 'utils/constants';
import { getTextFactory } from 'utils/TextUtils';

import StepsPopup from './components/StepsPopup';
import './styles.scss';

export type ClientRedirectNewProps = InjectedIntlProps & {
  history: History;
};

export let modalProps = {
  title: '',
  Content: <div />,
  headerClass: 'no-border-bottom',
  onClose: () => {},
  isCloseHidden: false,
  hasIcon: false,
  hasFooter: true,
  close: '',
};

const ClientRedirectNew: FunctionComponent<ClientRedirectNewProps> = ({ intl, history }) => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const getText = getTextFactory(intl, 'clientRedirectNew');

  useEffect(() => {
    scrollToTop('.dsl-form__scrollZone');
    focusBody();

    const bncId = getBncId();
    sendStepLoadedEvent('welcome', EVENT_IDS.FS, '', {
      eventInfo: {
        ucsId: 'UCS-bao-0001',
      },
      step: {
        stepId: '1',
        flowId: 'generic',
      },
      user: {
        bncId,
      },
    });

    manageAutomaticRedirect(intl.locale);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeModal = (): void => {
    setShowModal(false);
  };

  const handleLinkClick = () => {
    modalProps = {
      ...modalProps,
      title: '',
      Content: <StepsPopup intl={intl} />,
      hasFooter: true,
      close: getText('steps.buttonTitle'),
      onClose: closeModal,
    };

    setShowModal(true);
  };

  const goToCreateProfile = (): void => {
    history.push({ pathname: ROUTE.CREATE_USER_PROFILE });
  };

  const handleLoginRedirect = (): void => {
    sendClickAnalyticsEvent('welcome', 'Go to Login');
    redirectToUnifiedLogin(intl.locale);
  };

  return (
    <div className="layout-content">
      <div className="client-redirect-wrapper">
        <div className="flex items-center mb-6">
          <FinancialGrowthColor size={{ width: 80, height: 80 }} title="financial-growth-color" />
          <div className="spc-choix__subtitle">
            <Heading type="h2" size={5}>
              {getText('pageHeader')}
            </Heading>
          </div>
        </div>
        <Heading type="h1" className="mb-6">
          {getText('pageTitle')}
        </Heading>

        {/* Existing-client sign-in option */}
        <Card border="shadow" className="mb-2">
          <Row className="flex items-center">
            <Col xs={12} lg={8}>
              <Heading type={'span'} size={5}>
                {getText('hasAccess.cardTitle')}
              </Heading>
              <div className="mt-2 description">{getText('hasAccess.explanation')}</div>
            </Col>
            <Col xs={12} lg={4} className="button-container">
              <Button
                size="large"
                appearance="primary"
                name="sign-in-button"
                className="whitespace-nowrap"
                onClick={handleLoginRedirect}
              >
                {getText('hasAccess.buttonTitle')}
              </Button>
            </Col>
          </Row>
        </Card>

        {/* New-client profile creation option */}
        <Card border="shadow">
          <Row className="flex items-center">
            <Col xs={12} lg={8}>
              <Heading type={'span'} size={5}>
                {getText('hasNoAccess.cardTitle')}
              </Heading>
              <div className="mt-2 description">{getText('hasNoAccess.explanation')}</div>
            </Col>
            <Col xs={12} lg={4} className="button-container">
              <Button
                size="large"
                appearance="primary"
                name="create-profile-button"
                className="create-profile-button"
                onClick={goToCreateProfile}
              >
                {getText('openAccount.buttonTitle')}
              </Button>
            </Col>
          </Row>

          <div className="spc-choix__hiddenContent open-account-instructions">
            <Heading type="h3" size={6} className="mb-4 text-[1rem] mt-2">
              {getText('openAccount.generalExplanation')}
            </Heading>
            <ul className="spc-avantCommencerList mb-2">
              <li className="flex items-center pb-4">
                <Selfie title="selfie" size={{ width: 40, height: 40 }} className="spc-avantCommencerList__icon" />
                <div>
                  <Text appearance="muted" size="small">
                    {getText('openAccount.explanation2')}
                    &nbsp;
                    <Link underlined name="steps" onClick={handleLinkClick}>
                      {getText('openAccount.link2')}
                    </Link>
                  </Text>
                </div>
              </li>
              <li className="flex items-center pb-4">
                <Bank title="bank" size={{ width: 40, height: 40 }} className="spc-avantCommencerList__icon" />
                <div>
                  <Text appearance="muted" size="small">
                    {getText('openAccount.explanation3')}
                  </Text>
                </div>
              </li>

              <li className="flex items-center">
                <RelocatingEmployee
                  title="relocating-employee"
                  size={{ width: 40, height: 40 }}
                  className="spc-avantCommencerList__icon"
                />
                <div>
                  <Text appearance="muted" size="small">
                    {getText('openAccount.explanation4')}
                  </Text>
                </div>
              </li>
            </ul>
          </div>
        </Card>
        <div className="pending-request-link-wrapper">
          <Link underlined onClick={handleLoginRedirect}>
            {getText('pendingRequest.link')}
          </Link>
        </div>
      </div>
      {showModal && <Modal {...modalProps} name={'open:identity verification'} text={''} />}
    </div>
  );
};

export default ClientRedirectNew;
